import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";

import { DefaultCatchBoundary } from "~/components/default-catch-boundary";
import { DefaultNotFound } from "~/components/default-not-found";
import { DefaultPending } from "~/components/default-pending";
import { ApiError } from "~/lib/api";
import { endSession } from "~/lib/session";
import { routeTree } from "./routeTree.gen";

function signOutOnUnauthorized(error: unknown, handledElsewhere = false) {
  if (handledElsewhere || !(error instanceof ApiError) || error.status !== 401) return;

  void endSession(queryClient).then(() =>
    router.navigate({ to: "/inloggen", search: { melding: "verlopen" } }),
  );
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) =>
      signOutOnUnauthorized(error, query.meta?.handlesUnauthorized === true),
  }),
  mutationCache: new MutationCache({
    onError: (error) => signOutOnUnauthorized(error),
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 2,
      retry: (failureCount, error) =>
        failureCount < 3 &&
        !(error instanceof ApiError && error.status >= 400 && error.status < 500),
    },
  },
});

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: DefaultCatchBoundary,
  defaultNotFoundComponent: DefaultNotFound,
  defaultPendingComponent: DefaultPending,
  defaultPendingMs: 400,
  defaultPendingMinMs: 300,
  scrollRestoration: true,
  defaultStructuralSharing: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
